import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function PaymentStatus({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (cancelled || !sessionId) return;
    if (attempts >= 8 || status === "paid" || status === "failed") return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") setStatus("paid");
        else if (["failed", "expired"].includes(data.payment_status)) setStatus("failed");
        else setAttempts((a) => a + 1);
      } catch {
        setAttempts((a) => a + 1);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [attempts, sessionId, status, cancelled]);

  const icon = status === "paid" ? <CheckCircle size={48} weight="fill" className="text-[#10B981]" />
    : status === "failed" ? <XCircle size={48} weight="fill" className="text-[#EF4444]" />
    : status === "cancelled" ? <XCircle size={48} weight="fill" className="text-[#F59E0B]" />
    : <Clock size={48} weight="fill" className="text-[#F59E0B] animate-pulse" />;

  const msg = {
    paid: "Payment received. Thank you!",
    failed: "Payment failed. Please try again.",
    cancelled: "Payment was cancelled.",
    checking: `Confirming your payment... (${attempts + 1}/8)`,
  }[status] || "Checking status...";

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#0A0A0A] grid-bg">
      <div className="max-w-md w-full border border-[#262626] bg-[#141414] p-10 text-center" data-testid="payment-status-card">
        <div className="flex justify-center mb-6">{icon}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-3">{msg}</h1>
        {sessionId && (
          <p className="text-xs text-[#71717A] font-mono mb-6 break-all">{sessionId}</p>
        )}
        <Button onClick={() => navigate("/payments")} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="back-payments-btn">
          Back to Payments
        </Button>
      </div>
    </div>
  );
}
